import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarRange, Pencil, Plus } from 'lucide-react';
import Layout from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  type AdminClipperSeasonsQuery,
  useAdminClipperSeasonsQuery,
  useAdminConsolidateClipperSeasonMutation,
  useAdminCreateClipperSeasonMutation,
  useAdminSnapshotClipperSeasonStartMutation,
  useAdminUpdateClipperSeasonMutation,
} from '@/generated/graphql';
import { useToast } from '@/hooks/use-toast';
import { mapClipperSeasonError } from '@/lib/ranks/mapClipperSeasonError';

type ClipperSeason = AdminClipperSeasonsQuery['adminClipperSeasons'][number];

type SeasonForm = {
  name: string;
  startsAt: string;
  endsAt: string;
};

const emptyForm: SeasonForm = {
  name: '',
  startsAt: '',
  endsAt: '',
};

function toDatetimeLocal(iso: string): string {
  const date = new Date(iso);
  const timezoneOffset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
}

function toIsoDatetime(value: string): string {
  return new Date(value).toISOString();
}

export default function AdminClipperSeasons() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState<ClipperSeason | null>(null);
  const [form, setForm] = useState<SeasonForm>(emptyForm);

  const { data, loading, error, refetch } = useAdminClipperSeasonsQuery();
  const [createSeason, { loading: creating }] = useAdminCreateClipperSeasonMutation();
  const [updateSeason, { loading: updating }] = useAdminUpdateClipperSeasonMutation();
  const [snapshotSeason, { loading: snapshotting }] =
    useAdminSnapshotClipperSeasonStartMutation();
  const [consolidateSeason, { loading: consolidating }] =
    useAdminConsolidateClipperSeasonMutation();

  const seasons = data?.adminClipperSeasons ?? [];
  const isEditing = selectedSeason !== null;
  const datesLocked = selectedSeason?.start_snapshotted_at != null;

  const showMutationError = (mutationError: unknown) => {
    const message = mutationError instanceof Error ? mutationError.message : String(mutationError);
    const mappedError = mapClipperSeasonError(message);
    toast({
      title: t('admin.clipperSeasons.errors.generic'),
      description: mappedError === 'generic'
        ? undefined
        : t(`admin.clipperSeasons.errors.${mappedError}`),
      variant: 'destructive',
    });
  };

  const openCreateDialog = () => {
    setSelectedSeason(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (season: ClipperSeason) => {
    setSelectedSeason(season);
    setForm({
      name: season.name,
      startsAt: toDatetimeLocal(season.starts_at),
      endsAt: toDatetimeLocal(season.ends_at),
    });
    setDialogOpen(true);
  };

  const saveSeason = async () => {
    if (!form.name || !form.startsAt || !form.endsAt) return;

    try {
      if (selectedSeason) {
        await updateSeason({
          variables: {
            id: selectedSeason.documentId,
            name: form.name,
            starts_at: datesLocked ? undefined : toIsoDatetime(form.startsAt),
            ends_at: datesLocked ? undefined : toIsoDatetime(form.endsAt),
          },
        });
        toast({ title: t('admin.clipperSeasons.toast.updated') });
      } else {
        await createSeason({
          variables: {
            name: form.name,
            starts_at: toIsoDatetime(form.startsAt),
            ends_at: toIsoDatetime(form.endsAt),
          },
        });
        toast({ title: t('admin.clipperSeasons.toast.created') });
      }
      setDialogOpen(false);
      await refetch();
    } catch (mutationError) {
      showMutationError(mutationError);
    }
  };

  const snapshot = async (season: ClipperSeason) => {
    try {
      await snapshotSeason({ variables: { id: season.documentId } });
      toast({ title: t('admin.clipperSeasons.toast.snapshotted') });
      await refetch();
    } catch (mutationError) {
      showMutationError(mutationError);
    }
  };

  const consolidate = async (season: ClipperSeason) => {
    try {
      await consolidateSeason({ variables: { id: season.documentId } });
      toast({ title: t('admin.clipperSeasons.toast.consolidated') });
      await refetch();
    } catch (mutationError) {
      showMutationError(mutationError);
    }
  };

  return (
    <Layout>
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CalendarRange className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-2xl font-bold">{t('admin.clipperSeasons.title')}</h1>
                <p className="text-muted-foreground">{t('admin.clipperSeasons.subtitle')}</p>
              </div>
            </div>
            <Button onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              {t('admin.clipperSeasons.create')}
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t('admin.clipperSeasons.list')}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-muted-foreground">{t('admin.clipperSeasons.loading')}</p>
              ) : error ? (
                <p className="text-sm text-destructive">{t('admin.clipperSeasons.errors.generic')}</p>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('admin.clipperSeasons.columns.name')}</TableHead>
                        <TableHead>{t('admin.clipperSeasons.columns.window')}</TableHead>
                        <TableHead>{t('admin.clipperSeasons.columns.status')}</TableHead>
                        <TableHead className="w-[320px]">
                          {t('admin.clipperSeasons.columns.actions')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {seasons.map((season) => (
                        <TableRow key={season.id}>
                          <TableCell className="font-medium">{season.name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(season.starts_at).toLocaleString()} — {new Date(season.ends_at).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {t(`admin.clipperSeasons.status.${season.status}`)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditDialog(season)}
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                {t('admin.clipperSeasons.edit')}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={season.start_snapshotted_at != null || snapshotting}
                                onClick={() => snapshot(season)}
                              >
                                {t('admin.clipperSeasons.forceSnapshot')}
                              </Button>
                              <Button
                                size="sm"
                                disabled={
                                  season.start_snapshotted_at == null
                                  || season.consolidated_at != null
                                  || consolidating
                                }
                                onClick={() => consolidate(season)}
                              >
                                {t('admin.clipperSeasons.forceConsolidate')}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {seasons.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                            {t('admin.clipperSeasons.empty')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? t('admin.clipperSeasons.edit') : t('admin.clipperSeasons.create')}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? t('admin.clipperSeasons.editDescription')
                : t('admin.clipperSeasons.createDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="clipper-season-name">{t('admin.clipperSeasons.fields.name')}</Label>
              <Input
                id="clipper-season-name"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="clipper-season-starts-at">
                  {t('admin.clipperSeasons.fields.startsAt')}
                </Label>
                <Input
                  id="clipper-season-starts-at"
                  type="datetime-local"
                  disabled={datesLocked}
                  value={form.startsAt}
                  onChange={(event) => setForm({ ...form, startsAt: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="clipper-season-ends-at">{t('admin.clipperSeasons.fields.endsAt')}</Label>
                <Input
                  id="clipper-season-ends-at"
                  type="datetime-local"
                  disabled={datesLocked}
                  value={form.endsAt}
                  onChange={(event) => setForm({ ...form, endsAt: event.target.value })}
                />
              </div>
            </div>
            {datesLocked && (
              <p className="text-sm text-muted-foreground">
                {t('admin.clipperSeasons.datesLockedHelp')}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={creating || updating || !form.name || !form.startsAt || !form.endsAt}
              onClick={saveSeason}
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
